import React, { useState } from 'react';
import FormControl from '@mui/material/FormControl';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
  FormLabel,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import InputCheckbox from './CheckBox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Images from '../Utils/images';
import { notify } from '../Utils/toastify';
interface Props {
  id: string;
  labelText?: string;
  placeHolderText?: string;
  clearIcon?: boolean;
  name?: string;
  error?: any;
  helperText?: any;
  defaultValue?: string | any;
  disabled?: boolean;
  value?: any;
  isCountryField?: boolean;

  onChange?: any;
  options: any;
  control?: null | any;
  setValue?: any;
  isError?: boolean;
  multiple?: boolean;
  isCheckbox?: boolean;
  requiredField?: boolean;
  limit?: number;
  disableClearable?: boolean;
  keyName?: string;
  clearIcon?: boolean;
  errors?: any;
  height?: boolean;
  onChangeMethod?: any;
  addOptionText: string;
  onAdd?: any;
  isEvent?: any;
  isScroll?: boolean; // Flag to enable scroll pagination
  fetchMoreOptions?: any; // Function to fetch more data
  hasMoreOptions?: boolean; // Whether more options are available to load
  disableSearch?: boolean;
  fromAddCrewForm?: boolean;
}

/**
 * Renders a scrollable listbox component with children elements.
 * @example
 * ListboxComponent({children: <li>Item</li>, onScroll: handleScroll}, ref)
 * <ul>...</ul> with applied styles and props
 * @param {any} props - Props include children elements and onScroll event handler.
 * @param {React.Ref<HTMLUListElement>} ref - Reference to the HTMLUListElement.
 * @returns {JSX.Element} A JSX element representing a styled listbox.
 * @description
 *   - Overrides maxHeight to 350 and enables vertical scrolling.
 *   - Spreads additional props onto the ul element.
 */
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props: any,
  ref: React.Ref<HTMLUListElement>,
) {
  const { children, onScroll, ...other } = props;

  return (
    <ul
      ref={ref}
      {...other}
      onScroll={onScroll}
      style={{
        maxHeight: 350,
        overflowY: 'auto',
      }}
    >
      {children}
    </ul>
  );
});

/**
 * Renders an AutoComplete component using Material-UI, with various configurable options.
 * @example
 * MUIAutoComplete({ id: 'country-select', labelText: 'Select Country', options: countryOptions, isCountryField: true })
 * returns an AutoComplete component with options for countries
 * @param {Props} props - Object containing configuration properties for the AutoComplete component.
 * @returns {JSX.Element} The rendered AutoComplete component wrapped inside a FormControl.
 * @description
 *   - Handles infinite scrolling for loading more options by listening to the scroll event.
 *   - Supports both single and multiple selection modes and can include checkboxes in options.
 *   - Allows additional options to be added dynamically, and indicates loading state during fetching operations.
 *   - Can disable searching functionality for scenarios where input filtering is not desired.
 */
export default function MUIAutoComplete(props: Props) {
  const {
    id,
    labelText,
    placeHolderText,
    name = '',
    error,
    helperText,
    defaultValue,
    disabled,
    options,
    control,
    setValue,
    isCountryField = false,
    multiple,
    isCheckbox,
    requiredField,
    limit = 1,
    disableClearable = false,
    keyName = '',
    clearIcon,
    errors,
    height,
    onChangeMethod,
    addOptionText,
    onAdd,
    isEvent,
    isScroll,
    fetchMoreOptions,
    hasMoreOptions,
    disableSearch,
    fromAddCrewForm,
  } = props;

  //   console.log("Errors =>", error)
  const [label, setLabel] = React.useState(false);
  const [open, setOpen] = React.useState(true);

  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(2);
  const [hasMore, setHasMore] = useState(false);

  // const handleScroll = async (event: React.SyntheticEvent) => {
  //   const listboxNode = event.currentTarget
  //   console.log('=>>>>>>yes we are here')

  //   if (
  //     listboxNode.scrollTop + listboxNode.clientHeight >=
  //     listboxNode.scrollHeight
  //   ) {
  //     console.log('Not Working')

  //     if (
  //       isScroll &&
  //       fetchMoreOptions &&
  //       hasMoreOptions &&
  //       !hasMore &&
  //       !loadingMore
  //     ) {
  //       setLoadingMore(true)
  //       try {
  //         await fetchMoreOptions(`?page=${page}&limit=12`, (status: any) => {
  //           status && setHasMore(true)
  //           setLoadingMore(false)
  //         })
  //         setPage((page) => page + 1)
  //       } catch (error) {
  //         console.error('Error fetching more options:', error)
  //       } finally {
  //         // setLoadingMore(false)
  //       }
  //     }
  //   }
  // }

  /**
   * Handles loading more options when reaching the bottom of a scrollable container.
   * @example
   * sync(event)
   * No return value, just performs an action based on scroll position.
   * @param {React.SyntheticEvent} event - Represents the scroll event of the container.
   * @returns {void} No explicit return value, the function performs side effects.
   * @description
   *   - Detects if the scroll has reached near the bottom of the container.
   *   - Fetches more options if certain conditions are met (e.g., isScroll, fetchMoreOptions are true).
   *   - Updates state variables like loadingMore and page for dynamic fetching and UI updates.
   */
  const handleScroll = async (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget as HTMLElement;
    const { scrollTop, clientHeight, scrollHeight } = listboxNode;

    if (scrollTop + clientHeight >= scrollHeight - 20) {
      if (
        isScroll &&
        fetchMoreOptions &&
        hasMoreOptions &&
        !hasMore &&
        !loadingMore
      ) {
        setLoadingMore(true);
        try {
          await fetchMoreOptions(`?page=${page}&limit=12`, (status: any) => {
            if (!status) {
              setHasMore(true);
            }
          });
          setPage(prev => prev + 1);
        } catch (error) {
          console.error('Error fetching more options:', error);
        } finally {
          setLoadingMore(false);
        }
      }
    }
  };

  return (
    <FormControl
      fullWidth
      className={height ? 'customAutocomplete' : ''}
      error={error ? true : false}
    >
      <FormLabel
        className="formLabel"
        style={{ display: labelText ? 'block' : 'none' }}
      >
        {labelText} {requiredField ? <sup>*</sup> : null}{' '}
      </FormLabel>
      {/* {/ <InputLabel >{labelText}</InputLabel> /} */}
      {control ? (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue ? defaultValue : null}
          render={({ field }) => (
            <Autocomplete
              {...field}
              // open
              // disableCloseOnSelect={false}
              limitTags={limit}
              ListboxComponent={isScroll ? ListboxComponent : undefined}
              // ListboxProps={isScroll ? { onScroll: handleScroll } : {}}
              disableClearable={true}
              popupIcon={<KeyboardArrowDownIcon />}
              disableCloseOnSelect={multiple ? true : false}
              disablePortal
              disabled={disabled}
              multiple={multiple ? true : false}
              id="combo-box-demo"
              size="medium"
              placeholder={placeHolderText}
              isOptionEqualToValue={(option, value) =>
                option?.[keyName] === value?.[keyName]
              }
              getOptionLabel={option => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                if (typeof option[keyName] === 'string') return option[keyName];
                return ''; // fallback instead of JSON.stringify(option)
              }}
              value={field.value || null}
              //   defaultValue={defaultValue}
              ListboxProps={
                isScroll
                  ? {
                      onScroll: handleScroll,
                    }
                  : {}
              }
              renderOption={(props, option) => {
                if (option.isNew) {
                  return (
                    <li
                      {...props}
                      onMouseDown={e => {
                        // Prevent Autocomplete from selecting this special row on mouse interaction
                        e.preventDefault();
                      }}
                      onClick={e => {
                        // Make the entire row clickable to open the modal and do not select the option
                        e.preventDefault();
                        e.stopPropagation();
                        fromAddCrewForm
                          ? onAdd()
                          : isEvent
                          ? options?.length > 19
                            ? notify(
                                'You can not add more than 20 user type',
                                'error',
                              )
                            : onAdd()
                          : console.log('');
                      }}
                    >
                      <div className="add_in_select">
                        <img src={Images.ADD_CIRCLE} alt="New Create Studio" />{' '}
                        {addOptionText}
                      </div>
                    </li>
                  );
                }

                if (option?.isLoading) {
                  return (
                    <li {...props}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap="8px"
                      >
                        <CircularProgress size={20} />
                        Loading...
                      </Box>
                    </li>
                  );
                }

                return <li {...props}>{option?.[keyName] || ''}</li>;
              }}
              options={
                loadingMore
                  ? [...options, { isLoading: true }]
                  : isEvent
                  ? [{ name: addOptionText, isNew: true }, ...options]
                  : options
              }
              onChange={(event, newValue: any, reason) => {
                if (isEvent && newValue?.isNew) {
                  // If the special "Create New" option is chosen (e.g., via keyboard),
                  // trigger add flow and keep the field value unchanged
                  fromAddCrewForm ? onAdd() : onAdd && onAdd();
                  setValue(name, '');
                  return;
                } else if (newValue) {
                  setValue(name, newValue[keyName] || ''); // store string
                } else {
                  setValue(name, '');
                }

                onChangeMethod && onChangeMethod(newValue);
              }}
              onInputChange={(event, value, reason) => {
                if (reason === 'input' && value === '') {
                  setValue(name, '');
                }
              }}
              sx={{ width: '100%' }}
              renderInput={params => (
                <TextField
                  placeholder={placeHolderText}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    onKeyDown: event => {
                      if (disableSearch) {
                        event.preventDefault(); // Prevent typing when disableSearch is true
                      }
                    },
                  }}
                  //   error={error ? true : false}
                  error={error ? true : false}
                />
              )}
              ChipProps={{
                deleteIcon: <img src={Images.CHIP_DELETE} alt="" />,
              }}
            />
          )}
        />
      ) : (
        <Autocomplete
          // open
          // disableCloseOnSelect={false}
          clearIcon
          disablePortal
          limitTags={limit}
          disableClearable={disableClearable}
          disableCloseOnSelect={multiple}
          id="combo-box-demo"
          size="medium"
          options={options}
          placeholder={placeHolderText}
          multiple={multiple}
          sx={{ width: '100%' }}
          renderOption={
            isCheckbox
              ? (props, options, { selected }) => (
                  <li {...props}>
                    {isCheckbox ? (
                      <InputCheckbox
                        id={`check_${id}`}
                        name={`check_${name}`}
                        checked={selected}
                      />
                    ) : (
                      ''
                    )}
                    {options}
                  </li>
                )
              : undefined
          }
          renderInput={params => (
            <TextField
              placeholder={placeHolderText}
              {...params}
              error={error ? true : false}
              value={params?.inputProps?.value || ''}
            />
          )}
          ChipProps={{
            deleteIcon: <img src={Images.CHIP_DELETE} alt="" />,
          }}
        />
      )}

      <FormHelperText className={error ? 'Mui-error' : ''}>
        {error}
      </FormHelperText>
    </FormControl>
  );
}
